<template>
	<div id="monitor_buoy" class="base-popup monitor-graph" :style="css_props">
        <v-card class="base-popup__wrapper" v-if="Object.keys(timeseries_data).length !== 0">
            <div id="base-popup__capture_range" class="base-popup__container">
                <div class="base-popup__tooltip">
                    <div class="base-popup__tooltip-title">
                        <div>
                            <div class="base-popup__tooltip-title__city_info">
                                <h1>{{ station_title }}</h1>
                            </div>
                            <v-btn
                                class="base-popup__tooltip-close-btn"
                                color="white"
                                icon
                                @click="close_dialog"
                            >
                                <v-icon color="white">icon-close</v-icon>
                            </v-btn>
                        </div>
                    </div>
                    <div class="base-popup__tooltip-selection">
                        <div class="selection-input">
                            <v-select
                                v-model="variable_selected"
                                :items="variable_select"
                                id="tsgraph-select-type"
                                item-text="text"
                                item-value="value"
                                :label="$t('__monitor_weather.weather_variable')"
                            ></v-select>
                        </div>
                        <div v-if="variable_selected === $t('__rainfall.rainfall')" class="selection-input">
                            <v-select
                                v-model="rain_selected"
                                :items="rain_select"
                                id="tsgraph-select-type"
                                item-text="text"
                                item-value="value"
                                :label="$t('__timeseries.time_select_text')"
                            ></v-select>
                        </div>
                        <div class="selection-input">
                            <v-select
                                v-model="day_selected"
                                :items="day_select"
                                id="tsgraph-select-day"
                                item-text="text"
                                item-value="value"
                                :label="$t('__chart_common.day_select_text')"
                            ></v-select>  
                        </div>
                    </div>
                </div>
                <div class="base-popup__content">
                    <div class="base-popup__content-detail d-flex justify-space-between">
                        <div class="d-flex align-center">
                            {{ is_pad_width ? props_svg_sub_title.time : props_svg_sub_title.full_title }}
                            <v-btn v-if="!is_mobile_width"
                                id="tsgraph-reset-btn" 
                                icon
                            >
                                <v-icon color="black"
                                @click="reset_time_range">icon-autorenew</v-icon>
                            </v-btn>
                        </div>
                        <div v-if="!is_mobile_width">
                            <v-btn
                                id="tsgraph-download" 
                                @click="save_png"
                                icon
                            >
                                <v-icon color="black">icon-file-download</v-icon>
                            </v-btn>
                            <v-btn
                                class="close-btn"
                                color="black"
                                icon
                                @click="close_dialog"
                            >
                                <v-icon color="black">mdi-close</v-icon>
                            </v-btn>
                        </div>
                        <v-menu v-if="is_mobile_width" offset-y>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    color="black"
                                    dark
                                    icon
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    <v-icon>icon-more</v-icon>
                                </v-btn>
                            </template>
                            <v-list>
                                <v-list-item @click="reset_time_range">
                                    <v-icon color="black">icon-autorenew</v-icon>
                                    {{$t('__chart_common.reset_time_range')}}
                                </v-list-item>
                                <v-list-item @click="save_png">
                                    <v-icon color="black">icon-file-download</v-icon>
                                    {{$t('__chart_common.save_image')}}
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </div>
                    <div 
                        v-if="should_draw_histogram_diagram_header.includes(variable_selected)"
                        class="base-popup__content-legend"
                    >
                        <span class="legend histogram"/> {{ variable_selected }}
                        <span class="legend no-data-label">&#10010;</span> {{ $t('__chart_common.legend_no_data') }}
                    </div>
                    <div
                        v-else-if="should_draw_line_diagram_header.includes(variable_selected)"
                        class="base-popup__content-legend"
                    >
                        <span
                            class="legend" 
                            :class="{
                                line: variable_selected !== '風向',
                                circle: variable_selected === '風向'
                            }"
                        />
                        {{ variable_selected }}
                        <span class="legend no-data-label">&#10010;</span> {{ $t('__chart_common.legend_no_data') }}
                        <template v-if="need_extreme_value_headers.includes(variable_selected)">
                            <span class="legend triangle__wind-max" /> {{`${$t('__monitor_weather.legend_max')}${variable_selected}`}}
                            <span class="legend triangle__wind-min" /> {{`${$t('__monitor_weather.legend_min')}${variable_selected}`}}
                        </template>
                        <template v-if="variable_selected === $t('__monitor_weather.wind_speed')">
                            <span class="legend triangle__gust-max" /> {{`${$t('__monitor_weather.legend_max')}${$t('__monitor_weather.legend_gust')}`}}
                            <span class="legend cross">&#10010;</span> {{ $t('__monitor_weather.legend_gust') }}
                            <span class="legend gust_time">HH:MM</span>{{`${$t('__monitor_weather.legend_gust')}${$t('__monitor_weather.legend_time_text')}`}}
                        </template>
                    </div>
                    <div class="base-popup__content-graph">
                            <QpeHistogram 
                                v-if="should_draw_histogram_diagram_header.includes(variable_selected)"
                                ref="chart"
                                :choosed_time_series_data="choosed_time_series_data"
                                :x_axis_time_series_data="x_axis_time_series_data"
                                :svg_main_title="station_name"
                                :svg_sub_title="props_svg_sub_title.full_title"
                                :basic_layout_setting="basic_layout_setting"
                                :x_axis="x_axis"
                                :y_axis="y_axis"
                                :draw_custom_function="draw_custom_function"
                                :tooltip_value_text="tooltip_value_text"
                                :tooltip_value_unit="tooltip_value_unit"
                                :show_data_end_time="false"
                            />
                            <QpeLineChart
                                v-if="should_draw_line_diagram_header.includes(variable_selected)"
                                ref="chart"
                                :choosed_time_series_data="choosed_time_series_data"
                                :x_axis_time_series_data="x_axis_time_series_data"
                                :svg_main_title="station_name"
                                :svg_sub_title="props_svg_sub_title.full_title"
                                :basic_layout_setting="basic_layout_setting"
                                :x_axis="x_axis"
                                :y_axis="y_axis"
                                :draw_custom_function="draw_custom_function"
                                :show_path="show_path_headers.includes(variable_selected)"
                                :show_extreme_value="need_extreme_value_headers.includes(variable_selected)"
                                :tooltip_value_text="tooltip_value_text"
                                :tooltip_value_unit="tooltip_value_unit"
                                :tooltip_value_icon_name="tooltip_value_icon_name"
                                :show_tick_color="!no_basic_tick_color_headers.includes(variable_selected)"
                                :show_data_end_time="false"
                            />
                    </div>

                    <div class="time-slider">
                        <div class="time-slider__content-container">
                            <span 
                                class="time-slider__content-before" 
                                @click="change_dragged_index_to_first"
                            />
                            <v-slider
                                v-model="dragged_index"
                                :max="max_dragged_index"
                                :min="min_dragged_index"
                                class=time-slider__content
                                :tick-labels="slider_tick_labels"
                                ticks="always"
                                tick-size="4"
                            >

                            </v-slider>
                            <span 
                                class="time-slider__content-after" 
                                @click="change_dragged_index_to_last"
                            />
                        </div>
                        <span class="time-slider__icon-container">
                            <v-icon 
                                class="time-slider__icon"
                                @click="minus_dragged_index_a_unit"
                            >
                                icon-backward-10
                            </v-icon>
                            <v-icon 
                                class="time-slider__icon"
                                @click="plus_dragged_index_a_unit"
                            >
                                icon-forward-10
                            </v-icon>
                        </span>
                    </div>
                </div>
            </div>
        </v-card>
        <v-card v-else>
            <v-alert
                :value="true"
                type="error"
                class="alert"
            >
                暫時無法顯示歷史圖表
            </v-alert>
        </v-card>
    </div>
</template>

<script>
    import { 
        transform_datetime_str_to_chart_need_format, 
    } from '@/utils/common/custom_time.js'
    import { 
        judge_value_valid, 
        judge_extreme_value_valid 
    } from '@/utils/chart/verification.js'
    import { draw_cross_legend } from '@/utils/chart/graph.js'
    import { 
        draw_middle_text, 
        draw_wind_direction_middle_text_background,
        draw_wind_tier_middle_text_background 
    } from '@/utils/chart/d3_common.js'
    import debounce from 'lodash/debounce';
    import * as d3 from 'd3';
    import QpeHistogram from './qpe_histogram.vue'
    import QpeLineChart from './qpe_line_chart.vue'

	export default {
		name: 'monitor_buoy',
        
        components: {
            QpeHistogram, 
            QpeLineChart
        },

        props: {
            timeseries_data: {
                type: Object,
                default () {
                    return {}
                }
            },
            default_variable: {
                type: String,
                default () {
                    return ''
                }
            },
        }, 

		data() {
			return {
                variable_selected: this.default_variable,
                day_selected: '1',
                variable_select: ['平均風', '風向', '氣壓', '氣溫', '海溫', '浪高', '波浪週期', '浪向'],
                
                day_select: [
                    {'text':'1天', 'value':'1'}, {'text':'2天', 'value':'2'},
                    {'text':'3天', 'value':'3'}, {'text':'4天', 'value':'4'},
                    {'text':'5天', 'value':'5'}, {'text':'6天', 'value':'6'}
                ],
                
                variable_value_mapping: {
                    '平均風': {
                        text: '平均風(m/s)',
                        data_key: '平均風',
                        display_interval: 1,
                        ticks: 10,
                    },
                    '風向': {
                        text: '風向',
                        data_key: '風向',
                        default_max: 360,
                        default_min: 0,
                        display_interval: 3,
                        ticks: 36,
                        icon: 'icon-wind',
                    },
                    '氣壓': {
                        text: '氣壓(hPa)',
                        data_key: '氣壓',
                        plus: 10,
                        minus: 10,
                        nan_max: 1030,
                        nan_min: 1000,
                        display_interval: 1,
                        ticks: 10,
                    },
                    '氣溫': {
                        text: '氣溫(℃)',
                        data_key: '氣溫',
                        plus: 10,
                        minus: 5,
                        nan_max: 35,
                        nan_min: 15,
                        display_interval: 1,
                        ticks: 10,
                    },
                    '海溫': {
                        text: '海溫(℃)',
                        data_key: '海溫',
                        plus: 10,
                        minus: 5,
                        nan_max: 35,
                        nan_min: 15,
                        display_interval: 1,
                        ticks: 10,
                    },
                    '浪高': {
                        text: '浪高(m)',
                        data_key: '浪高',
                        plus: 3,
                        nan_max: 4,
                        default_min: 0,
                        display_interval: 1,
                        ticks:10,
                    },
                    '波浪週期': {
                        text: '波浪週期(秒)',
                        data_key: '波浪週期',
                        plus: 3,
                        nan_max: 8,
                        default_min: 0,
                        display_interval: 1,
                        ticks:10,
                    },
                    '浪向': {
                        text: '浪向',
                        data_key: '浪向',
                        default_max: 360,
                        default_min: 0,
                        display_interval: 3,
                        ticks: 36,
                        icon: 'icon-wind',
                    }
                },

                x_axis: {
                    'label': '時間',
                    'ticks': 48,
                    'display_interval': 2,
                    'label_distance': 40
                },
                
                basic_layout_setting: {
                    'margin': {
                        'top': 50,
                        'right': 50,
                        'bottom': 50,
                        'left': 30,
                    }
                },

                dragged_index: Object.keys(this.timeseries_data['data_time']).length > 0 ? this.timeseries_data['data_time'].length : 0,

                should_draw_histogram_diagram_header: ['浪高', '波浪週期'],
                should_draw_line_diagram_header: ['平均風', '風向', '氣壓', '氣溫', '海溫', '浪向'],
                show_path_headers: ['平均風', '氣壓', '氣溫', '海溫'],
                no_basic_tick_color_headers: ['平均風', '風向', '浪向'],
                need_extreme_value_headers: ['平均風', '氣壓', '氣溫', '海溫'],
            }
        },
        
		computed: {
            css_props() {
                const get_total_slider_width = () => {
                    const is_mobile  = window.matchMedia("(max-width: 767px)").matches;
                    const is_pad = window.matchMedia("(max-width: 1024px)").matches;

                    if (is_mobile) {
                        return 75
                    }
                    
                    if(is_pad) {
                        return 90
                    }
                    
                    return 55
                }

                const total_slider_width = get_total_slider_width()

                const interval_count =  6 + 1 - this.day_selected
                const slider_thumb_width = (total_slider_width) / interval_count
                const slider_width = total_slider_width - slider_thumb_width
                const slider_pseudo_width = slider_thumb_width / 2
                return {
                    '--slider-thumb-width': `${slider_thumb_width}vw`,
                    '--slider-width': `${slider_width}vw`,
                    '--slider-pseudo-width': `${slider_pseudo_width}vw`,
                }
            },

            ten_min_index_list() {
                return this.timeseries_data['data_time']
                    .slice(this.dragged_index - 1 - 144*this.day_selected, this.dragged_index)
                    .map((datum) => {
                        const date_type_datum = new Date(datum)
                        return date_type_datum.getMinutes() === 0
                    })
            },

            choosed_time_series_data() {
                return this.timeseries_data[this.variable_value_mapping[this.variable_selected].data_key]
                    .slice(this.dragged_index - 1 - 144*this.day_selected, this.dragged_index)
                    .filter((datum, index) => this.ten_min_index_list[index]) 
            },

            gust_wind_data() {
                return this.timeseries_data['瞬間風']
                    .slice(this.dragged_index - 1 - 144*this.day_selected, this.dragged_index)
                    .filter((datum, index) => this.ten_min_index_list[index]) 
            },

            time_list() {
                return this.timeseries_data['data_time']
            },

            x_axis_time_series_data() {
                return this.time_list
                    .slice(this.dragged_index - 1 - 144*this.day_selected, this.dragged_index)
                    .filter((datum, index) => this.ten_min_index_list[index])
            },

            max_dragged_index() {
                return this.time_list.length
            },

            station_title() {
                return `${this.timeseries_data['station_name']}  ${this.timeseries_data['station_code']}`
            },

            station_name() {
                return `${this.timeseries_data['station_name']}站`
            },

            props_svg_sub_title() {
                const station = `${this.variable_selected}時序組體圖`
                const start_datetime = transform_datetime_str_to_chart_need_format(this.x_axis_time_series_data[0])
                const end_datetime = transform_datetime_str_to_chart_need_format(this.x_axis_time_series_data[this.x_axis_time_series_data.length-1])
                return {
                    full_title: `${station} ${start_datetime} ~ ${end_datetime}`,
                    station: `${station}`,
                    time: `${start_datetime} ~ ${end_datetime}`
                }
            },

            y_axis() {
                const { text, ticks, display_interval } = this.variable_value_mapping[this.variable_selected]
                return {
                    'label': text,
                    'ticks': ticks,
                    'display_interval': display_interval,
                    'label_distance': 50
                }
            },

            tooltip_value_text() {
                const { text } = this.variable_value_mapping[this.variable_selected]
                return text.split('(')[0]
            },

            tooltip_value_unit() {
                const { text } = this.variable_value_mapping[this.variable_selected]
                if (text.includes('(')) {
                    return text.split('(')[1].replace(')', '')
                }
                return ''
            },

            tooltip_value_icon_name() {
                const { icon } = this.variable_value_mapping[this.variable_selected]
                return icon
            },

            min_dragged_index() {
                return this.max_dragged_index - 144*(6 - this.day_selected)
            },

            slider_tick_labels() {
                const valid_timeseries_data = this.time_list
                    .slice(this.min_dragged_index, this.max_dragged_index)
                    .map((time, index) => {
                        if (index % 144 === 72) {
                            const prev_day_num = (6 - this.day_selected) - ((index-72) / 144)
                            if (prev_day_num > 0) {
                                return `${prev_day_num}天前`
                            }
                        }
                        return ''
                    })
                return valid_timeseries_data
            },

            is_mobile_width() {
                return window.innerWidth < 769
            },

            is_pad_width() {
                return window.innerWidth < 1025
            },
        },

        watch: {
            // dragged_index() {
            //     this.debounce_call_rerender_function()
            // },

            default_variable: {
                immediate: true,
                handler() {
                    this.variable_selected = this.default_variable
                }
            },

            choosed_time_series_data: {
                immediate: true,
                handler() {
                    this.calc_y_axis_max_min()
                    this._call_rerender_function()
                },
            }
        },
        
        mounted() {
            this.dragged_index = this.time_list.length;
        },

		methods: {
            reset_time_range() {
                this.day_selected = '1'
                this.dragged_index = this.timeseries_data['data_time'].length
            },

            change_dragged_index_to_first() {
                this.dragged_index = this.min_dragged_index
            },

            change_dragged_index_to_last() {
                this.dragged_index = this.max_dragged_index
            },

            minus_dragged_index_a_unit() {
                // v-model + v-slider 會幫忙把值鎖住
                this.dragged_index--
            },

            plus_dragged_index_a_unit() {
                this.dragged_index++
            },

            debounce_call_rerender_function: debounce(function() { // 不能 arrow function
                this._call_rerender_function()
            }, 150),

            _call_rerender_function() {
                setTimeout(() => {
                    this.$refs.chart.rerender_svg()
                }, 0);
            },

            close_dialog() {
                this.$emit('close-chart-dialog');
            },

            save_png() {
                const { station_name, station_code, data_time } = this.timeseries_data
                const file_name = `${station_name}_${station_code}_${d3.timeFormat('%Y%m%d')(new Date())}_${data_time[data_time.length-1]}`
                this.$refs.chart.save_png(file_name)
            },

            calc_y_axis_max_min() {
                const { default_max, default_min, nan_max, nan_min , plus, minus } = this.variable_value_mapping[this.variable_selected]
                const ignore_None_data = this.choosed_time_series_data.filter(datum => judge_value_valid(datum))
                if (this.variable_selected === '平均風') {
                    const gust_ignore_None_data = this.gust_wind_data.filter(datum => judge_value_valid(datum)) 
                    return this._calc_y_axis_max_min_for_average_wind([...ignore_None_data, ...gust_ignore_None_data])
                }
                
                if (default_max !== undefined) {
                    this.y_axis.max_value = default_max
                } else {
                    const max_data_value = ignore_None_data.length > 0 ? Math.max(...ignore_None_data) : NaN
                    this.y_axis.max_value = judge_extreme_value_valid(max_data_value) ? max_data_value + plus : nan_max
                }

                if(default_min !== undefined) {
                    this.y_axis.min_value = default_min
                } else {
                    const min_data_value = ignore_None_data.length > 0 ? Math.min(...ignore_None_data) : NaN
                    this.y_axis.min_value = judge_extreme_value_valid(min_data_value) ? min_data_value - minus : nan_min
                }
            },

            _calc_y_axis_max_min_for_average_wind(ignore_None_data) {
                const max_data_value = Math.max(...ignore_None_data)
                this.y_axis.max_value = max_data_value >= 32.7 ? 56.1 : max_data_value >= 10.8 ? 37.0 : 13.9;
                this.y_axis.min_value = 0
            },

            draw_custom_function(svg, width, x, y, tooltip) {
                const margin = this.basic_layout_setting.margin
                const end_datetime_string = this.x_axis_time_series_data[this.x_axis_time_series_data.length-1]
                if(['風向', '浪向'].includes(this.variable_selected)) {
                    const WIND_DIR_ANGLE = [
                        { text: 'N', value: 0 }, { text: '', value: 45 },
                        { text: 'E', value: 90 }, { text: '', value: 135 },
                        { text: 'S', value: 180 }, { text: '', value: 225 },
                        { text: 'W', value: 270 }, { text: '', value: 315 },
                        { text: 'N', value: 360 },
                    ]
                    const TEXT_OFFSET = -2
                    draw_middle_text({
                        svg, x, y, end_datetime_string,
                        text_offset: TEXT_OFFSET,
                        data: WIND_DIR_ANGLE, 
                    })
                    draw_wind_direction_middle_text_background({ svg, margin, width })
                } else if(this.variable_selected === '平均風') {
                    const margin = this.basic_layout_setting.margin
                    const TEXT_OFFSET = -2

                    const WIND_TIER_SPEED = [
                        0, 0.3, 1.6, 3.4, 5.5, 8.0, 10.8, 13.9, 17.2, 20.8, 24.5, 28.5, 32.7, 37.0, 41.5, 46.2, 51.0, 56.1
                    ]
                    const draw_tier = WIND_TIER_SPEED
                        .map((speed, index) => {
                            if (index === 0) {
                                return {
                                    text: '',
                                    value: speed
                                }
                            }

                            return {
                                text: `${index} 級`,
                                value: speed
                            }
                        })
                        .filter(data => data.value <= this.y_axis.max_value)
                    
                    draw_middle_text({
                        svg, x, y, end_datetime_string,
                        text_offset: TEXT_OFFSET,
                        data: draw_tier, 
                    }) 
                    draw_wind_tier_middle_text_background({svg, margin, width})

                    this.day_selected === '1' && this._draw_gust(svg, width, x, y, tooltip)
                    this._draw_gust_max(svg, width, x, y, tooltip)
                }
            },

            _draw_gust(svg, width, x, y, tooltip){
                const margin = this.basic_layout_setting.margin
                const time_parse = d3.timeParse('%Y-%m-%d %H:%M:%S')
                const gust_timeseries_data = this.x_axis_time_series_data
                const is_gust_value_valid = d => judge_value_valid(d)
                // 十字
                svg.selectAll('path.gust_speed')
                    .data(this.gust_wind_data)
                    .enter()
                    .append('path')
                    .attr('class', 'gust_speed')
                    .attr('d', d3.symbol().type(d3.symbolCross))
                    .attr('transform', (d, i) => {
                        if (is_gust_value_valid(d)) {
                            return `translate(${margin.left + x(time_parse(gust_timeseries_data[i]))}, ${y(d)}) scale(0.9)`;
                        }
                    })
                    .style('stroke-width', 0)
                    .style('fill', 'blue')
                    .style('opacity', (d,i) => is_gust_value_valid(d, i) ? 1: 0)
                    .on('mouseover', (d, i) => {
                        const gust_time_value = transform_datetime_str_to_chart_need_format(gust_timeseries_data[i])
                        tooltip
                            .transition()
                            .duration(200)
                            .style('opacity', 0.9);
                        tooltip
                            .html(`
                                ${gust_time_value}
                                <br>
                                ${this.$t('__monitor_weather.gust_value')}：${parseFloat(this.gust_wind_data[i])} m/s
                            `)
                            .style('left', `${margin.left + x(time_parse(gust_timeseries_data[i]))}px`)
                            .style('top', `${d3.event.offsetY + 50}px`) 
                    })
                    .on('mouseout', () => {
                        tooltip.transition()
                            .duration(500)
                            .style('opacity', 0);
                    });

                svg.selectAll('text.gust_time')
                    .data(this.gust_wind_data)
                    .enter()
                    .append('text')
                    .attr('class', 'gust_time')
                    .attr('x', (d, i) => {
                        return margin.left + x(time_parse(gust_timeseries_data[i]));
                    })
                    .attr('y', (d) => {
                        return y(d) - 7.5;
                    })
                    .text((d, i) => {
                        if (is_gust_value_valid(d, i)) {
                            return gust_timeseries_data[i].slice(10, 16)
                        }
                    })
                    .style('text-anchor', 'middle')
                    .style('fill', '#B319EF')
                    .style('font-size', 12)
                    .style('font-weight', 'bold');
            },

            _draw_gust_max(svg, width, x, y, tooltip) {
                const margin = this.basic_layout_setting.margin
                let label_distance, label_value_distance, rotate_deg
                const time_parse = d3.timeParse('%Y-%m-%d %H:%M:%S')
                const gust_timeseries_data = this.x_axis_time_series_data

                const float_gust_data = this.gust_wind_data
                    .map((value) => parseFloat(value))

                // 有可能會超出時間範圍，所以找第一個在時間範圍內的最大值
                const max_value = float_gust_data
                    .filter(d => !isNaN(d))
                    .sort((prev, next) => next - prev)
                    .find(datum => {
                        const datum_index = float_gust_data.lastIndexOf(datum)
                        return x(time_parse(gust_timeseries_data[datum_index])) >= 0
                    })

                const max_value_index = float_gust_data.lastIndexOf(max_value)
                const max_value_time = gust_timeseries_data[max_value_index]
                
                label_distance = -40
                label_value_distance = -55
                rotate_deg = 180

                const triangle = d3.symbol()
                    .type(d3.symbolTriangle)
                    .size(150)

                svg.append('path')
                    .attr('d', triangle)
                    .attr('fill', 'blue')
                    .attr('transform', `translate(${margin.left + x(time_parse(max_value_time))}, ${y(max_value)+ label_distance}) rotate(${rotate_deg})`)
                    .on('mouseover', () => {
                        const max_gust_time_value = transform_datetime_str_to_chart_need_format(max_value_time)
                        tooltip
                            .transition()
                            .duration(200)
                            .style('opacity', 0.9)
                        tooltip
                            .html(`
                                ${max_gust_time_value}
                                </br>
                                ${this.$t('__monitor_weather.gust_value')}：${parseFloat(max_value)} m/s
                            `)
                            .style('left', `${margin.left + x(time_parse(max_value_time))}px`)
                            .style('top', `${d3.event.offsetY + 50}px`)        
                    })
                    .on('mouseout', () => {
                        tooltip.transition()
                            .duration(500)
                            .style('opacity', 0);
                    })
                
                svg.append('text')
                    .attr('x', margin.left + x(time_parse(max_value_time)))
                    .attr('y', y(max_value) + label_value_distance)
                    .attr('text-anchor', 'middle')
                    .style('text-decoration', 'underline')  
                    .style('stroke', '#B319EF')
                    .style('opacity', 0.75)
                    .text(max_value)
            },
        },
	}
</script>

<style lang="scss" scoped>
@import '../../../css/base_popup.scss';

.legend {
    margin: 2px 5px;
}

.histogram {
    height: 1rem;
    width: 1rem;
    background-color: #4682b4;
    display: inline-block;
}

.no-data-label{
    color: red;
    font-weight: bold;
    transform: rotate(45deg);
}

.line {
    width: 2rem;
    border-top: 5px solid #4682b4;
    margin-bottom: 4px;
    display: inline-block;
}

@mixin triangle-base {
    width: 0px;
    height: 0px;
    border-left: 9px solid transparent;
    border-right: 9px solid transparent;
}

.triangle {
    &__wind-max {
        @include triangle-base;
        border-top: 15px solid #ff0000;
    }

    &__wind-min {
        @include triangle-base;
        border-bottom: 15px solid #ffa500;
    }

    &__gust-max {
        @include triangle-base;
        border-top: 15px solid #0000ff;
    }
}

.circle {
    height: 1rem;
    width: 1rem;
    background-color: #4682b4;
    display: inline-block;
    border-radius: 1rem;
}

.cross {
    color: #0000ff;
}

.gust_time {
    color: purple;
    font-size: 0.8rem;
}

.back-station-title {
    margin-bottom: 0.25rem;

    @include laptop {
        position: absolute;
        top: 0;
        left: 16px;
    }

    &__text {
        font-size: 0.75rem;
        line-height: 1.33;
        letter-spacing: 0.5px;

        @include laptop {
            display: none;
        }
    }
}

.base-popup__tooltip-title__city_info {
    margin-left: 0.25rem;
}

</style>

<style lang="scss">

@mixin time_slider_psuedo_comment {
    content: '';
    position: absolute;
    width: var(--slider-pseudo-width);
    margin-top: 12px;
    height: 8px;
    background: #e8ecf7;
    border-radius: 9ex;
}

.base-popup {
    .time-slider {
        width: 100%;
        text-align: center;

        &__content {
            &-container {
                text-align: center;
                display: inline-block;
                margin-left: calc(1rem + var(--slider-pseudo-width));
            }

            display: inline-block;
            position: relative;
            width: var(--slider-width);

            &-before {
                @include time_slider_psuedo_comment;
                transform: translateX(calc(-100% + 1rem));
            }

            &-after {
                @include time_slider_psuedo_comment;
                transform: translateX(- 1rem);
                // z-index: -1;
            }
        }

        &__icon {
            transform: translateY(-50%);

            &::before {
                margin: 0 0.1rem;
            }

            &-container {
                margin-left: calc(var(--slider-pseudo-width) - 0.5rem);
            }
        }
    }
            
    }

    #monitor_buoy .v-slider__thumb {
        width: var(--slider-thumb-width);
        height: 8px;
        border-radius: 12px;
        box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.32);
        background-image: linear-gradient(to right, #124ed0 0%, #2366f8 99%);
        transform: translate(-50%, -50%);
        z-index: 1;

        &::before {
            content: none;
        }

        &::after {
            content: 'lll';
            color: #ffffff;
            position: absolute;
            transform: translate(calc(var(--slider-thumb-width) / 2 - 50%) , -40%);
        }
    }

    #monitor_buoy .v-slider__track-container {
        height: 8px !important;
        margin: 0 0 0 1px;
        background-color: #e8ecf7;

        .v-slider__track-background {
            background-color: #e8ecf7 !important;
        }

        .v-slider__track-fill {
            background-color: #e8ecf7 !important;
        }
    }

    .v-slider__ticks-container {
        .v-slider__tick {
            background-color: #e8ecf7 !important;
            
            &--filled {
                background-color: #e8ecf7 !important;
            }

            &-label {
                height: 14px;
                font-family: NotoSansCJKtc;
                font-size: 11px;
                line-height: 1.27;
                letter-spacing: 0.5px;
                color: #b3b7c4;
            }
        }
}

.back-station-title {
    .v-btn--icon.v-size--default {
        width: 1.5rem;
    }

    .v-btn::before {
        transform: translateX(-0.65rem);
        width: 2rem;
    }

    .v-btn__content {
        margin-right: 0.75rem;
    }
}
</style>